/**
 * Unit factory - creates unit entities with all required components
 */

import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import { createPosition } from '@/game/components/Position';
import { createMovement } from '@/game/components/Movement';
import { createHealth } from '@/game/components/Health';
import { createCombat } from '@/game/components/Combat';
import { createFaction } from '@/game/components/Faction';
import { createUnit } from '@/game/components/Unit';
import { createRender } from '@/game/components/Render';
import { createCollider, getUnitCollisionRadius } from '@/game/components/Collider';
import { UNIT_STATS, FACTIONS } from '@/game/factions/FactionData';
import type { UnitType, FactionId, Entity } from '@/types';

export function createUnitEntity(
  world: World,
  unitType: UnitType,
  factionId: FactionId,
  x: number,
  y: number,
  z: number
): Entity {
  const entity = world.createEntity();
  const stats = UNIT_STATS[unitType];
  const faction = FACTIONS[factionId];

  // Apply faction bonuses to stats
  const moveSpeed = stats.moveSpeed * (faction?.speedBonus ?? 1);

  world.addComponent(entity, ComponentTypes.Position, createPosition(x, y, z));
  world.addComponent(entity, ComponentTypes.Movement, createMovement(moveSpeed));
  world.addComponent(entity, ComponentTypes.Health, createHealth(stats.health));
  world.addComponent(
    entity,
    ComponentTypes.Combat,
    createCombat(stats.attack, stats.defense, stats.range, stats.attackSpeed)
  );
  world.addComponent(entity, ComponentTypes.Faction, createFaction(factionId));
  world.addComponent(entity, ComponentTypes.Unit, createUnit(unitType));
  world.addComponent(entity, ComponentTypes.Render, createRender());

  // Add collider for collision detection
  const collisionRadius = getUnitCollisionRadius(unitType);
  world.addComponent(entity, ComponentTypes.Collider, createCollider(collisionRadius, false, 1.0));

  return entity;
}
